import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import {
  Container,
  Header,
  BackButton,
  Title,
  FacilityList,
  FacilityCard,
  FacilityImage,
  FacilityOverlay,
  FacilitySubtitle,
  FacilityTitle,
  FacilityBuilding,
  LeftInfo,
  RightInfo
} from './Study.styles';
import apiClient from 'services/api';

interface Facility {
  id: number;
  name: string;
  description: string;
  imageUrl: string;
  building?: {
    id: number;
    name: string;
  };
}

const categoryMap: Record<string, { apiName: string; displayName: string }> = {
  '/study': { apiName: 'STUDY', displayName: '스터디' },
  '/semina': { apiName: 'SEMINAR', displayName: '세미나' },
  '/sportfacilities': { apiName: 'SPORTS', displayName: '체육시설' },
  '/culturespace': { apiName: 'CULTURE', displayName: '문화공간' },
  '/printerpc': { apiName: 'PRINT_PC', displayName: '프린터/PC' },
};

function Study() {
  const navigate = useNavigate();
  const location = useLocation();
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const currentCategory = categoryMap[location.pathname] || categoryMap['/study'];

  useEffect(() => {
    const fetchFacilities = async () => {
      try {
        setIsLoading(true);
        const universityId = localStorage.getItem('universityId');
        const response = await apiClient.get<Facility[]>(`/universities/${universityId}/facilities`, {
          params: { category: currentCategory.apiName },
        });
        setFacilities(response.data);
      } catch (error) {
        console.error('Failed to fetch facilities:', error);
        setFacilities([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFacilities();
  }, [currentCategory.apiName]);

  const handleGoBack = () => {
    navigate(-1);
  };

  const handleCardClick = (id: number) => {
    navigate(`/facilities/${id}`);
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={handleGoBack}>
          <FontAwesomeIcon icon={faChevronLeft} />
        </BackButton>
        <Title>{currentCategory.displayName}</Title>
      </Header>
      <FacilityList>
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>Loading...</div>
        ) : facilities.length > 0 ? (
          facilities.map((facility) => (
            <FacilityCard key={facility.id} onClick={() => handleCardClick(facility.id)}>
              <FacilityImage src={facility.imageUrl} alt={facility.name} />
              <FacilityOverlay>
                <LeftInfo>
                  <FacilitySubtitle>{facility.description}</FacilitySubtitle>
                  {facility.building && (
                    <FacilityBuilding>{facility.building.name}</FacilityBuilding>
                  )}
                </LeftInfo>
                <RightInfo>
                  <FacilityTitle>{facility.name}</FacilityTitle>
                </RightInfo>
              </FacilityOverlay>
            </FacilityCard>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>등록된 시설이 없습니다.</div>
        )}
      </FacilityList>
    </Container>
  );
}

export default Study;
